import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { NoDivisionsBanner } from '@/components/events/NoDivisionsBanner';
import {
  ArrowLeft, Calendar, MapPin, Users, DollarSign,
  Receipt, AlertCircle, Award, FileText, Pencil,
  AlertTriangle, CheckCircle2, Circle,
} from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { useOrgModules } from "@/contexts/OrgModulesContext";
import HotelAssignmentPanel from '@/components/admin/event-detail/HotelAssignmentPanel';


const TIER_LABEL: Record<string, string> = {
  showcase: 'Showcase',
  club_priority: 'Club Priority',
  priority: 'Priority',
  standard: 'Standard',
};

const STATUS_TONE: Record<string, string> = {
  pending_review: 'bg-amber-100 text-amber-900',
  pending_payment: 'bg-orange-100 text-orange-900',
  approved: 'bg-emerald-100 text-emerald-900',
  honored: 'bg-emerald-100 text-emerald-900',
  denied: 'bg-rose-100 text-rose-900',
  violated: 'bg-rose-100 text-rose-900',
};

const fmtMoney = (n: number) => `$${Number(n ?? 0).toFixed(2)}`;
const fmtDate = (s: string | null) =>
  s ? new Date(s + 'T12:00:00').toLocaleDateString() : '';
const timeAgo = (iso: string | null) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

export default function OperatorEventOverviewPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { isEnabled } = useOrgModules();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: res, error: fnError } = await invokeEdgeFunction(
          'rle-operator-event-overview',
          { body: { event_id: eventId } },
        );
        if (fnError || !res?.ok) {
          setError(res?.error?.message || fnError?.message || 'Could not load event.');
        } else {
          setData(res.data);
        }
      } catch (e: any) {
        setError(e?.message ?? 'Unexpected error');
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-900 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { event, registrations, financials, constraints, roster, recent } = data;

  // Navigation helpers — operator routes take eventId in the path; extra filters as query params.
  const inboxBase = `/admin-dashboard/events/${eventId}/scheduling-requests`;
  const regsBase = `/admin/events/${eventId}/registrations`;
  const goInbox = (qs = '') => navigate(`${inboxBase}${qs}`);
  const goRegs = (qs = '') => navigate(`${regsBase}${qs}`);
  const goRegistrationDetail = (id: string) => navigate(`/club-dash/registration/${id}`);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-slate-600 hover:text-slate-900 mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </button>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{event.name}</h1>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {fmtDate(event.start_date)} – {fmtDate(event.end_date)}
            </span>
            {event.venue_name && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {event.venue_name}
                {event.venue_city ? `, ${event.venue_city}` : ''}
                {event.venue_state ? `, ${event.venue_state}` : ''}
              </span>
            )}
            {event.event_type_name && (
              <span className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                {event.event_type_name}
              </span>
            )}
            {event.status && <Badge variant="secondary">{event.status}</Badge>}
          </div>
        </div>
      </div>

      <NoDivisionsBanner eventId={eventId} status={event.status} />

      {/* Top KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiTile
          icon={<Users className="h-5 w-5 text-slate-400" />}
          label="Teams registered"
          value={String(registrations.unique_teams ?? registrations.total)}
          sub={`${registrations.total} registrations`}
          onClick={() => goRegs()}
        />
        <KpiTile
          icon={<DollarSign className="h-5 w-5 text-slate-400" />}
          label="Collected"
          value={fmtMoney(financials.total_collected)}
          sub={
            financials.total_outstanding > 0
              ? `${fmtMoney(financials.total_outstanding)} outstanding`
              : 'All paid'
          }
        />
        <KpiTile
          icon={<Award className="h-5 w-5 text-slate-400" />}
          label="Roster size"
          value={String(roster.total_players)}
          sub="across all teams"
        />
        <KpiTile
          icon={<AlertCircle className="h-5 w-5 text-slate-400" />}
          label="Constraints"
          value={String(constraints.total)}
          sub={`${constraints.by_status.pending_review ?? 0} pending review`}
          onClick={() => goInbox()}
        />
      </div>

      {/* Schedule setup checklist */}
      {data.schedule_setup && !data.schedule_setup.is_complete && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-700" />
                  <h2 className="text-base font-semibold text-amber-900">Schedule setup incomplete</h2>
                </div>
                <p className="text-xs text-amber-800 mt-1">
                  This event isn't ready for scheduling. Configure these before generating a schedule:
                </p>
                <ul className="mt-3 space-y-1.5">
                  <li className="flex items-center gap-2 text-sm">
                    {data.schedule_setup.courts_count > 0
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <Circle className="h-4 w-4 text-amber-600" />}
                    <span className={data.schedule_setup.courts_count > 0 ? 'text-slate-700' : 'font-medium'}>
                      Courts / fields {data.schedule_setup.courts_count > 0 ? `(${data.schedule_setup.courts_count})` : '— not configured'}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {data.schedule_setup.sessions_count > 0
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <Circle className="h-4 w-4 text-amber-600" />}
                    <span className={data.schedule_setup.sessions_count > 0 ? 'text-slate-700' : 'font-medium'}>
                      Time slots / sessions {data.schedule_setup.sessions_count > 0 ? `(${data.schedule_setup.sessions_count})` : '— not configured'}
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    {data.schedule_setup.format_configured
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <Circle className="h-4 w-4 text-amber-600" />}
                    <span className={data.schedule_setup.format_configured ? 'text-slate-700' : 'font-medium'}>
                      Format {data.schedule_setup.format_configured ? `(${(data.schedule_setup.format || '').replace(/_/g, ' ')})` : '— not configured'}
                    </span>
                  </li>
                </ul>
              </div>
              <Button
                onClick={() => navigate(`/admin-dashboard/events/${eventId}/schedule-setup`)}
                size="sm"
              >
                Configure schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* By tier */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Registrations by tier
            </h2>
            {registrations.by_tier.length === 0 ? (
              <p className="text-sm text-slate-500">No registrations yet.</p>
            ) : (
              <div className="space-y-3">
                {registrations.by_tier.map((t: any) => (
                  <div
                    key={t.tier}
                    onClick={() =>
                      goRegs(`?tier=${encodeURIComponent(t.tier)}`)
                    }
                    className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 cursor-pointer hover:bg-slate-50 transition rounded px-1 -mx-1"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">
                        {TIER_LABEL[t.tier] ?? t.tier}
                      </span>
                      <span className="text-sm text-slate-500">
                        {t.count} {t.count === 1 ? 'team' : 'teams'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">{fmtMoney(t.revenue)}</p>
                      {t.outstanding > 0 && (
                        <p className="text-xs text-orange-700">
                          {fmtMoney(t.outstanding)} due
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By division */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Registrations by division
            </h2>
            {registrations.by_division.length === 0 ? (
              <p className="text-sm text-slate-500">No registrations yet.</p>
            ) : (
              <div className="space-y-2">
                {registrations.by_division.map((d: any) => (
                  <div
                    key={d.division}
                    onClick={() =>
                      goRegs(`?division=${encodeURIComponent(d.division)}`)
                    }
                    className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 cursor-pointer hover:bg-slate-50 transition rounded px-1 -mx-1"
                  >
                    <span className="text-slate-800">{d.division}</span>
                    <span className="font-medium text-slate-900">{d.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Constraints summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Scheduling requests</h2>
            <div className="flex items-center gap-3">
              {constraints.total_fees > 0 && (
                <span className="flex items-center gap-1 text-sm text-slate-600">
                  <Receipt className="h-4 w-4" />
                  Fees: {fmtMoney(constraints.total_fees)}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={() => navigate(`/admin-dashboard/events/${eventId}/conflicts`)}>
                Analyze conflicts
              </Button>
            </div>
          </div>
          {constraints.total === 0 ? (
            <p className="text-sm text-slate-500">No scheduling requests yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(constraints.by_status).map(([status, count]) =>
                (count as number) > 0 ? (
                  <span
                    key={status}
                    onClick={() =>
                      goInbox(`?status=${encodeURIComponent(status)}`)
                    }
                    className={`px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-slate-50 transition ${
                      STATUS_TONE[status] ?? 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    {String(count)} {status.replace(/_/g, ' ')}
                  </span>
                ) : null,
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hotel partner assignment */}
      {isEnabled?.('hotels') !== false && <HotelAssignmentPanel eventId={eventId} />}

      {/* Recent activity: two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Recent registrations
            </h2>
            {recent.registrations.length === 0 ? (
              <p className="text-sm text-slate-500">No registrations yet.</p>
            ) : (
              <div className="space-y-3">
                {recent.registrations.map((r: any) => (
                  <div
                    key={r.id ?? `${r.team_name}-${r.registered_at}`}
                    onClick={() => r.id && goRegistrationDetail(r.id)}
                    className="flex items-start justify-between border-b border-slate-100 pb-2 last:border-0 cursor-pointer hover:bg-slate-50 transition rounded px-1 -mx-1"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{r.team_name}</p>
                      <p className="text-xs text-slate-500">
                        {r.division}
                        {r.tier && <> · {TIER_LABEL[r.tier] ?? r.tier}</>}
                        {' · '}
                        {timeAgo(r.registered_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        {fmtMoney(r.amount_paid)}
                        {r.amount_due > r.amount_paid && (
                          <span className="text-slate-400"> / {fmtMoney(r.amount_due)}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">{r.payment_status}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Recent constraint requests
            </h2>
            {recent.constraints.length === 0 ? (
              <p className="text-sm text-slate-500">No constraint requests yet.</p>
            ) : (
              <div className="space-y-3">
                {recent.constraints.map((c: any) => (
                  <div
                    key={c.id ?? `${c.team_name}-${c.created_at}`}
                    onClick={() =>
                      goInbox(c.id ? `?constraint_id=${encodeURIComponent(c.id)}` : '')
                    }
                    className="flex items-start justify-between border-b border-slate-100 pb-2 last:border-0 cursor-pointer hover:bg-slate-50 transition rounded px-1 -mx-1"
                  >
                    <div>
                      <p className="font-medium text-slate-800 capitalize">
                        {c.constraint_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.team_name} · {timeAgo(c.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      {c.fee_charged > 0 && (
                        <p className="font-medium text-slate-900">{fmtMoney(c.fee_charged)}</p>
                      )}
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          STATUS_TONE[c.status] ?? 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        {c.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiTile({
  icon,
  label,
  value,
  sub,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  onClick?: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      className={onClick ? 'cursor-pointer hover:bg-slate-50 transition' : undefined}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-slate-500">{label}</p>
          {icon}
        </div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}
